import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Inline indeterminate spinner — the single source for "this action is in
 * flight" affordances (submit buttons, refresh icons, small inline waits).
 * Wraps lucide's `Loader2` with `animate-spin` baked in so call sites never
 * hand-roll `<Loader2 className="h-4 w-4 animate-spin" />` again.
 *
 * Per DESIGN_GUIDELINES §5: this is for tiny inline regions only. For a full
 * content panel use `<TabLoadingState>`; for a sub-1s auth/redirect handoff
 * use `<FullPageSpinner>` (the centered page-gate ring) — not this.
 *
 * Pick `size` to match the surrounding text/icon; pass `className` for the
 * contextual bits the size can't know (leading margin `mr-1.5`, `text-muted-
 * foreground`, etc.). Decorative by default (`aria-hidden`) since it almost
 * always sits beside its own label ("Saving…"); pass `aria-hidden={false}`
 * + a `role`/label for a standalone indicator.
 */
const SIZES = {
	xs: "h-2.5 w-2.5",
	sm: "h-3 w-3",
	md: "h-3.5 w-3.5",
	lg: "h-4 w-4",
	xl: "h-5 w-5",
} as const;

export function Spinner({
	size = "md",
	className,
	...props
}: {
	size?: keyof typeof SIZES;
	className?: string;
} & React.ComponentProps<typeof Loader2>) {
	return (
		<Loader2
			aria-hidden="true"
			className={cn(SIZES[size], "animate-spin", className)}
			{...props}
		/>
	);
}
